/**
 * Industries CTA Component
 * 
 * An engaging, animated call to action section with premium design elements,
 * following the adapter pattern architecture and offering full theme support.
 */
'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, useAnimation, useInView, animate, useMotionValue } from 'framer-motion';
import { 
  Card, 
  Button,
  fadeInVariants,
  scaleUpVariants,
  containerVariants,
  itemVariants,
  buttonVariants
, Badge, Text } from '@/components/ui/design-system';
// import { PremiumIcon } from '@/components/icons/PremiumIcon';
import { ArrowRight, Check, ArrowUpRight, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AnimatedStatProps {
  end: number;
  duration?: number;
  label: string;
  isInView: boolean;
  prefix?: string;
  suffix?: string;
}

// Enhanced feature item component with improved animations
const FeatureItem = ({ text, index = 0 }: { text: string; index?: number }) => (
  <motion.div 
    className="flex items-center group"
    initial={{ opacity: 0, x: -10 }}
    whileInView={{ opacity: 1, x: 0 }}
    transition={{ 
      duration: 0.4, 
      delay: 0.1 * index, 
      ease: [0.22, 1, 0.36, 1]
    }}
    viewport={{ once: true }}
  >
    <div className="flex-shrink-0 w-6 h-6 bg-red-600 dark:bg-red-500 rounded-full flex items-center justify-center mr-3">
      <Check className="w-3.5 h-3.5 text-white" />
    </div>
    <span className="text-sm text-slate-700 dark:text-slate-300 transition-colors duration-200">
      {text}
    </span>
  </motion.div>
);

// Enhanced stat display component with premium animations
const AnimatedStat = ({ end, duration = 2, label, isInView, prefix = '', suffix = '' }: AnimatedStatProps) => {
  const countMotionValue = useMotionValue(0);
  const [displayText, setDisplayText] = React.useState(prefix + '0' + suffix);

  React.useEffect(() => {
    const unsubscribe = countMotionValue.on("change", (latestValue) => {
      setDisplayText(prefix + Math.round(latestValue) + suffix);
    });
    return () => {
      unsubscribe();
    };
  }, [countMotionValue, prefix, suffix]);

  React.useEffect(() => {
    if (isInView) {
      const animationControls = animate(countMotionValue, end, { duration });
      return () => {
        animationControls.stop();
      };
    }
  }, [isInView, end, duration, countMotionValue]);

  return (
    <motion.div 
      className="text-center"
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ 
        duration: 0.5, 
        delay: 0, 
        ease: [0.22, 1, 0.36, 1] 
      }}
      viewport={{ once: true }}
    >
      <div className="text-xl sm:text-2xl md:text-3xl font-bold premium-gradient-text mb-1 relative">
        <motion.span
          className="absolute -top-1 -right-2 text-primary-500 dark:text-primary-400"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 5, 0]
          }}
          transition={{ 
            duration: 3,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <Sparkles className="h-4 w-4" />
        </motion.span>
        {displayText}
      </div>
      <Text variant="body" className="text-xs md:text-sm text-gray-600 dark:text-gray-300">
        {label}
      </Text>
    </motion.div>
  );
};

// Subtle particle animation for enhanced visual effect
const FloatingParticle = ({ 
  delay = 0, 
  size = 6, 
  left = 0, 
  opacity = 0.3,
  seed = 0 
}: { 
  delay?: number, 
  size?: number, 
  left?: number, 
  opacity?: number,
  seed?: number
}) => {
  // Use a deterministic offset based on the seed for consistent client/server values
  const xOffset = Math.abs(Math.sin(seed)) * 20; 
  
  return (
    <motion.div 
      className="absolute bg-primary-400/20 dark:bg-primary-500/20 rounded-full"
      style={{
        width: size,
        height: size,
        left: `${left}%`,
        top: '50%',
        opacity: 0,
      }}
      animate={{
        y: [0, -80, 0],
        x: [0, xOffset - 10, 0], // Deterministic x movement
        opacity: [0, opacity, 0],
      }}
      transition={{
        duration: 10 + (seed % 5), // Deterministic duration
        delay: delay,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut"
      }}
    />
  );
};

export default function IndustriesCTA() {
  // Features list
  const features = [
    "No minimum order quantity",
    "Industry-specific packaging solutions",
    "Expert consultation included",
    "Fast turnaround times"
  ];

  // Create refs for staggered animation sequence
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  // Use predefined particle configs instead of random values
  const particleConfigs = [
    { delay: 0, size: 6, left: 15, opacity: 0.2, seed: 1 },
    { delay: 1.5, size: 8, left: 30, opacity: 0.3, seed: 2 },
    { delay: 3, size: 5, left: 45, opacity: 0.2, seed: 3 },
    { delay: 4.5, size: 9, left: 60, opacity: 0.25, seed: 4 },
    { delay: 6, size: 7, left: 75, opacity: 0.3, seed: 5 },
    { delay: 7.5, size: 5, left: 25, opacity: 0.2, seed: 6 },
    { delay: 9, size: 8, left: 55, opacity: 0.3, seed: 7 },
    { delay: 10.5, size: 6, left: 70, opacity: 0.25, seed: 8 }
  ];

  // Create particles using fixed configs instead of random generation
  const particles = particleConfigs.map((config, i) => (
    <FloatingParticle 
      key={i} 
      delay={config.delay}
      size={config.size}
      left={config.left}
      opacity={config.opacity}
      seed={config.seed}
    />
  ));

  return (
    <section className="py-14 sm:py-20 relative overflow-hidden from-primary-50 via-amber-50 to-primary-50 dark:bg-slate-900">
      {/* Enhanced background elements with animated gradients */}
      <div className="absolute inset-0 overflow-hidden -z-10">
        <div className="absolute inset-0 bg-[url('/noise.png')] opacity-[0.02] dark:opacity-[0.03] mix-blend-overlay pointer-events-none transform-gpu"></div>
        
        <motion.div 
          className="absolute -top-40 -right-40 w-96 h-96 bg-primary-300/10 dark:bg-primary-600/10 premium-radius-full blur-3xl transform-gpu"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.6, 0.8, 0.6],
            rotate: [0, 5, 0]
          }}
          transition={{ 
            duration: 12,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        ></motion.div>
        
        <motion.div 
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-amber-300/10 dark:bg-amber-600/10 premium-radius-full blur-3xl transform-gpu"
          animate={{ 
            scale: [1, 1.15, 1],
            opacity: [0.6, 0.9, 0.6],
            rotate: [0, -5, 0]
          }}
          transition={{ 
            duration: 15, 
            delay: 1,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        ></motion.div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-4 md:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <Card 
            className={cn(
              "p-0 mx-auto max-w-6xl overflow-hidden border-0 relative premium-card-elevated premium-shadow-xl rounded-[24px]"
            )}
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
              {/* Left Content - Enhanced with premium animations */}
              <div 
                ref={containerRef}
                className="md:col-span-7 bg-white dark:bg-black p-6 sm:p-8 md:p-12 lg:p-16 flex flex-col justify-center relative overflow-hidden lg:premium-radius-l-3xl"
              >
                
                
                
                {/* Floating particles for ambient animation */}
                {particles}
                
                
                
                <motion.div 
                  className="relative z-10"
                  variants={containerVariants}
                  initial="hidden"
                  animate={controls}
                >
                  {/* Enhanced badge with premium animation using Badge component */}
                  <Badge variant="premiumShimmer" size="lg" className="mb-6" radius="full">
                    <span className="relative flex h-2 w-2 mr-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-80"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
                    </span>
                    <span>Custom Solutions</span>
                  </Badge>
                  
                  {/* Enhanced heading with premium animation */}
                  <motion.h2 
                    variants={fadeInVariants}
                    className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white relative overflow-visible"
                  >
                    <span className="inline-block relative mr-2">
                      <motion.span 
                        className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-primary-500 to-amber-500 rounded-full"
                        initial={{ width: 0 }}
                        whileInView={{ width: '100%' }}
                        transition={{ duration: 1, delay: 0.7, ease: "easeOut" }}
                        viewport={{ once: true }}
                      />
                      <motion.span
                        initial={{ y: 40, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        viewport={{ once: true }}
                        className="inline-block"
                      >
                        Can't Find Your 
                      </motion.span>
                    </span>
                    <span className="inline-block relative">
                      <motion.span
                        initial={{ y: 40, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        viewport={{ once: true }}
                        className="inline-block relative premium-gradient-text whitespace-nowrap"
                      >
                        Industry?
                      </motion.span>
                    </span>
                  </motion.h2>
                  
                  {/* Enhanced description with premium animation */}
                  <motion.p 
                    variants={fadeInVariants}
                    className="text-gray-600 dark:text-gray-300 text-lg mb-8 leading-relaxed max-w-xl"
                  >
                    We serve many more industries and continuously innovate to meet specific packaging needs. 
                    Our team of experts is ready to discuss your unique requirements and develop a tailored solution.
                  </motion.p>
                  
                  {/* Features list with enhanced staggered animation */}
                  <motion.div 
                    variants={containerVariants}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8 mb-8"
                  >
                    {features.map((feature, index) => (
                      <motion.div 
                        key={feature} 
                        variants={itemVariants}
                        custom={index}
                      >
                        <FeatureItem text={feature} index={index} />
                      </motion.div>
                    ))}
                  </motion.div>
                  
                  {/* Enhanced CTAs with premium animations */}
                  <motion.div 
                    variants={containerVariants}
                    className="flex flex-row flex-wrap gap-4 mt-8"
                  >
                    <motion.div
                      variants={scaleUpVariants}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                      className="flex-1 min-w-[160px]"
                    >
                      <Link href="/contact" className="block w-full">
                        <Button 
                          variant="primary"
                          size="lg"
                          endIcon={<ArrowUpRight className="h-4 w-4" />}
                          className="w-full"
                        >
                          Contact Us
                        </Button>
                      </Link>
                    </motion.div>
                    
                    <motion.div
                      variants={scaleUpVariants}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                      className="flex-1 min-w-[160px]"
                    >
                      <Link href="/about" className="block w-full">
                        <Button 
                          variant="secondary"
                          size="lg"
                          endIcon={<ArrowRight className="h-4 w-4" />}
                          className="w-full"
                        >
                          Learn About Us
                        </Button>
                      </Link>
                    </motion.div>
                  </motion.div>
                </motion.div>
              </div>
              
              {/* Right Image with enhanced animations and overlays */}
              <div className="md:col-span-5 h-64 sm:h-80 md:h-auto relative overflow-hidden rounded-b-3xl md:rounded-r-3xl md:rounded-b-none">
                {/* Enhanced image effect */}
                <div className="absolute inset-0 from-primary-500/20 to-primary-500/40 dark:bg-none z-10 mix-blend-overlay"></div>
                  <div className="absolute inset-0 hidden dark:block z-10 pointer-events-none rounded-b-3xl md:rounded-r-3xl md:rounded-b-none"></div>
                
                <motion.div
                  initial={{ scale: 1.1, opacity: 0.8 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
                  viewport={{ once: true }}
                  className="h-full w-full relative"
                >
                  <Image 
                    src="/images/pages/industries/cta/custom-packaging.jpg" 
                    alt="Custom Packaging Solutions"
                    fill
                    className="object-cover w-full h-full shadow-2xl rounded-b-3xl md:rounded-r-3xl md:rounded-b-none"
                  />
                  <div className="absolute inset-0 hidden dark:block bg-black/40 pointer-events-none rounded-b-3xl md:rounded-r-3xl md:rounded-b-none"></div>
                </motion.div>
                
                {/* Enhanced stats card with premium animation */}
                <motion.div 
                  className="absolute bottom-6 right-6 bg-white/90 dark:bg-gray-800/90 p-1.5 sm:p-2 md:p-3 rounded-xl shadow-lg backdrop-blur-sm z-20 max-w-[120px] sm:max-w-[150px] md:max-w-none"
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    y: -5,
                    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
                    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
                  }}
                >
                  <div className="flex flex-col gap-1.5 sm:gap-2 md:gap-3">
                    <AnimatedStat end={9} label="Years of Excellence" isInView={isInView} />
                    <div className="h-px w-full bg-gradient-to-r from-[var(--premium-gradient-from)] to-[var(--premium-gradient-to)]"></div>
                    <AnimatedStat end={100} label="Satisfied Clients" isInView={isInView} />
                  </div>
                </motion.div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
} 